import { Route } from "lucide-react";

/**
 * TripSheet — freight-table view mirroring the transporter's operational format
 * (From, To, Trip Type, Container Type, Size, Cargo Weight).
 * Freight Charges column is intentionally omitted from the on-screen view
 * (user fills that in Excel after download).
 */
export default function TripSheet({ result, vehicles, trip }) {
  const sizeByType = Object.fromEntries(
    (vehicles || []).map((v) => [v.type, v.size_label || ""])
  );

  const rows = (result.allocations || []).map((a, i) => ({
    idx: i + 1,
    from: trip.from_location || "—",
    to: trip.to_location || "—",
    tripType: trip.trip_type || "One Way",
    containerType: a.vehicle_name,
    size: sizeByType[a.vehicle_type] || "—",
    weightMt: (a.used_weight_kg / 1000).toFixed(2),
  }));

  return (
    <div className="bg-[#F8FAFC] border-2 border-[#2563EB]/60" data-testid="trip-sheet">
      <div className="p-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-9 h-9 bg-[#2563EB] flex items-center justify-center">
          <Route className="w-5 h-5 text-black" strokeWidth={2.5} />
        </div>
        <div className="flex-1">
          <div className="overline">Trip Sheet</div>
          <div className="heading text-lg font-semibold">Loading Plan (Trailer / Truck-wise)</div>
        </div>
        <div className="mono text-xs text-[#64748B] uppercase tracking-widest">
          {rows.length} trip{rows.length !== 1 ? "s" : ""}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFFFF]">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-3 overline w-10">#</th>
              <th className="text-left px-3 py-3 overline">From</th>
              <th className="text-left px-3 py-3 overline">To</th>
              <th className="text-left px-3 py-3 overline">Trip Type</th>
              <th className="text-left px-3 py-3 overline">Container Type</th>
              <th className="text-left px-3 py-3 overline">Size</th>
              <th className="text-right px-3 py-3 overline">Cargo Weight</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={`trip-${r.idx ?? i}-${r.containerType ?? "row"}`}
                className="border-b border-slate-100 hover:bg-white/[0.03] transition-colors duration-150"
                data-testid={`trip-row-${i}`}
              >
                <td className="px-3 py-3 mono text-[#2563EB] font-bold">{r.idx}</td>
                <td className="px-3 py-3 mono">{r.from}</td>
                <td className="px-3 py-3 mono">{r.to}</td>
                <td className="px-3 py-3 mono">{r.tripType}</td>
                <td className="px-3 py-3 mono">{r.containerType}</td>
                <td className="px-3 py-3 mono text-[#64748B]">{r.size}</td>
                <td className="px-3 py-3 mono text-right">{r.weightMt} mt</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-[#64748B]">
                  No trips to plan.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
